import { Component, OnInit, signal, computed, effect, input, output, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { ObjectOccurrence, ObjectOccurrenceData } from "../data.ingestion.service";
import { SpaceService } from "../../../services/space.service";
import type { SpaceAvailabilityRequest, SpConflict, SpReservation, S25WsSpace } from "../../../services/space.service";
import { NameService } from "../../../services/name.service";
import { UserprefService } from "../../../services/userpref.service";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { Proto } from "../../../pojo/Proto";
import { S25Util } from "../../../util/s25-util";
import { Item } from "../../../pojo/Item";

interface LocationSearchResult {
    locationId: number;
    locationName: string;
    availableCount: number;
    availableOccurrenceUuids: string[];
}

interface SearchItem {
    query_id?: number;
    query?: string;
    val?: number | string;
    query_name?: string;
    [key: string]: unknown;
}

@TypeManagerDecorator("s25-ng-object-occurrence-field")
@Component({
    selector: "s25-ng-object-occurrence-field",
    template: `
        <div class="object-occurrence-field">
            <!-- New Occurrence Input -->
            @if (!readOnly()) {
                <div class="new-occurrence-section">
                    <h4>Add New Occurrence</h4>
                    <div class="datetime-inputs">
                        <div class="datetime-group">
                            <label>Start Date & Time:</label>
                            <input type="datetime-local" [(ngModel)]="newStartDate" class="datetime-input" />
                        </div>
                        <div class="datetime-group">
                            <label>End Date & Time:</label>
                            <input type="datetime-local" [(ngModel)]="newEndDate" class="datetime-input" />
                        </div>
                        <s25-ng-button [type]="'primary'" [onClick]="addOccurrence" class="add-occurrence-btn">
                            Add Occurrence
                        </s25-ng-button>
                    </div>
                    @if (dateWarning()) {
                        <div class="warning-message">{{ dateWarning() }}</div>
                    }
                </div>
            }

            <!-- Location Selection (search dropdown and text search) -->
            @if (!readOnly() && occurrences().length > 0) {
                <div class="location-selection-global">
                    <div class="search-controls">
                        <div class="search-method-selector">
                            <label class="search-method-label">Search Method:</label>
                            <div class="search-method-options">
                                <label class="search-method-option">
                                    <input
                                        type="radio"
                                        name="searchMethod"
                                        [value]="'saved'"
                                        [checked]="searchMethod() === 'saved'"
                                        (change)="onSearchMethodChange('saved')"
                                    />
                                    <span>Saved Search</span>
                                </label>
                                <label class="search-method-option">
                                    <input
                                        type="radio"
                                        name="searchMethod"
                                        [value]="'name'"
                                        [checked]="searchMethod() === 'name'"
                                        (change)="onSearchMethodChange('name')"
                                    />
                                    <span>Search by Name</span>
                                </label>
                            </div>
                        </div>

                        @if (searchMethod() === "saved") {
                            <div class="search-dropdown-section">
                                <label>Select Saved Search:</label>
                                <s25-ng-search-dropdown
                                    [itemTypeId]="Item.Ids.Location"
                                    [onSelect]="onSearchSelected"
                                    [chosen]="selectedSearch()"
                                >
                                </s25-ng-search-dropdown>
                                @if (selectedSearch() !== undefined) {
                                    <s25-ng-button
                                        [type]="'outline'"
                                        [onClick]="clearSearchSelection"
                                        class="clear-search-btn"
                                    >
                                        Clear
                                    </s25-ng-button>
                                }
                            </div>
                        }

                        @if (searchMethod() === "name") {
                            <div class="search-text-section">
                                <label>Enter Location Name:</label>
                                <div class="text-search-input-group">
                                    <input
                                        type="text"
                                        class="text-search-input"
                                        [(ngModel)]="nameSearchText"
                                        (keyup.enter)="searchByName()"
                                        placeholder="Enter location name..."
                                    />
                                    <s25-ng-button [type]="'primary'" [onClick]="searchByName" class="search-btn">
                                        Search
                                    </s25-ng-button>
                                </div>
                            </div>
                        }
                    </div>

                    @if (searching()) {
                        <div class="search-loading">Searching locations...</div>
                    }

                    @if (locationResults().length > 0) {
                        <div class="location-results">
                            <h5>Available Locations</h5>
                            <ul class="location-results-list">
                                @for (result of locationResults(); track result.locationId) {
                                    <li class="location-result-item">
                                        <div class="location-info">
                                            <span class="location-name">{{ result.locationName }}</span>
                                            <span class="availability-badge"
                                                >({{ result.availableCount }}/{{ totalOccurrences() }})</span
                                            >
                                        </div>
                                        <s25-ng-button
                                            [type]="'primary'"
                                            [onClick]="
                                                addLocationToAvailable.bind(
                                                    this,
                                                    result.locationId,
                                                    result.availableOccurrenceUuids,
                                                    result.locationName
                                                )
                                            "
                                            class="add-location-btn"
                                        >
                                            Add
                                        </s25-ng-button>
                                    </li>
                                }
                            </ul>
                        </div>
                    } @else if (hasSearched() && !searching()) {
                        <div class="no-results">No available locations found</div>
                    }
                </div>
            }

            <!-- Existing Occurrences -->
            @if (occurrences().length > 0) {
                <div class="occurrences-list">
                    <h4>Occurrences</h4>
                    @for (occ of occurrences(); track occ.occurrenceUuid) {
                        <div class="occurrence-item">
                            <div class="occurrence-header">
                                <div class="occurrence-dates">
                                    <strong>{{ formatDatetime(occ.startDate) }}</strong> to
                                    <strong>{{ formatDatetime(occ.endDate) }}</strong>
                                </div>
                                @if (!readOnly()) {
                                    <s25-ng-button
                                        [type]="'danger--outline'"
                                        [onClick]="removeOccurrence.bind(this, occ.occurrenceUuid)"
                                        class="remove-btn"
                                    >
                                        <s25-ng-icon [type]="'close'" />
                                    </s25-ng-button>
                                }
                            </div>

                            <!-- Selected Locations (with remove buttons) -->
                            @if (occ.locationIds.length > 0) {
                                <div class="selected-locations">
                                    <strong>Selected Locations:</strong>
                                    <ul>
                                        @for (locId of occ.locationIds; track locId) {
                                            <li class="location-item">
                                                <span>{{ getLocationName(locId) }}</span>
                                                @if (!readOnly()) {
                                                    <s25-ng-button
                                                        [type]="'danger--outline'"
                                                        [onClick]="removeLocation.bind(this, occ.occurrenceUuid, locId)"
                                                        class="remove-location-btn"
                                                    >
                                                        <s25-ng-icon [type]="'close'" />
                                                    </s25-ng-button>
                                                }
                                            </li>
                                        }
                                    </ul>
                                </div>
                            } @else {
                                <div class="no-locations">
                                    <em>No locations selected for this occurrence</em>
                                </div>
                            }
                        </div>
                    }
                </div>
            }
        </div>
    `,
    styles: `
        .object-occurrence-field {
            padding: 1rem;
            border: 1px solid #ddd;
            border-radius: 4px;
            background-color: #f9f9f9;
        }

        .new-occurrence-section {
            margin-bottom: 2rem;
            padding-bottom: 1rem;
            border-bottom: 2px solid #ddd;
        }

        .datetime-inputs {
            display: flex;
            gap: 1rem;
            align-items: flex-end;
            flex-wrap: wrap;
        }

        .datetime-group {
            flex: 1;
            min-width: 200px;
        }

        .datetime-group label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
        }

        .datetime-input {
            width: 100%;
            padding: 0.5rem;
            border: 1px solid #ccc;
            border-radius: 4px;
        }

        .warning-message {
            color: #856404;
            background-color: #fff3cd;
            border: 1px solid #ffeaa7;
            padding: 0.75rem;
            border-radius: 4px;
            margin-top: 0.5rem;
        }

        .occurrence-item {
            border: 1px solid #ddd;
            border-radius: 4px;
            padding: 1rem;
            margin-bottom: 1rem;
            background-color: white;
        }

        .occurrence-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
        }

        .occurrence-dates {
            flex: 1;
        }

        .location-selection-global {
            margin: 1rem 0;
            padding: 1rem;
            background-color: #f0f8ff;
            border-radius: 4px;
            border: 1px solid #b0d4ff;
        }

        .search-controls {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1rem;
        }

        .search-method-selector {
            padding-bottom: 1rem;
            border-bottom: 1px solid #ddd;
        }

        .search-method-label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
        }

        .search-method-options {
            display: flex;
            gap: 1.5rem;
        }

        .search-method-option {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            cursor: pointer;
        }

        .search-method-option input[type="radio"] {
            margin: 0;
            cursor: pointer;
        }

        .search-dropdown-section,
        .search-text-section {
            width: 100%;
        }

        .clear-search-btn {
            margin-top: 0.5rem;
        }

        .location-selection-global label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
        }

        .text-search-input-group {
            display: flex;
            gap: 0.5rem;
        }

        .text-search-input {
            flex: 1;
            padding: 0.5rem;
            border: 1px solid #ccc;
            border-radius: 4px;
        }

        .search-btn {
            white-space: nowrap;
        }

        .search-loading {
            padding: 0.5rem;
            color: #666;
            font-style: italic;
        }

        .location-results {
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid #ddd;
        }

        .location-results h5 {
            margin: 0 0 0.5rem 0;
            font-size: 1rem;
        }

        .location-results-list {
            list-style: none;
            padding: 0;
            margin: 0;
        }

        .location-result-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem;
            margin-bottom: 0.5rem;
            background-color: white;
            border: 1px solid #ddd;
            border-radius: 4px;
        }

        .location-info {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex: 1;
        }

        .location-name {
            font-weight: 500;
        }

        .availability-badge {
            color: #666;
            font-size: 0.9rem;
        }

        .add-location-btn {
            white-space: nowrap;
        }

        .no-results {
            padding: 1rem;
            text-align: center;
            color: #999;
            font-style: italic;
        }

        .no-locations {
            margin-top: 1rem;
            padding: 0.5rem;
            color: #999;
            font-style: italic;
        }

        .selected-locations {
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid #eee;
        }

        .selected-locations strong {
            display: block;
            margin-bottom: 0.5rem;
        }

        .selected-locations ul {
            list-style: none;
            padding: 0;
            margin: 0;
        }

        .location-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem;
            background-color: #f8f9fa;
            border-radius: 4px;
            margin-bottom: 0.5rem;
        }

        .location-item span {
            flex: 1;
        }

        .add-occurrence-btn {
            height: fit-content;
        }

        .remove-btn,
        .remove-location-btn {
            height: fit-content;
            padding: 0.25rem 0.5rem;
        }

        h4 {
            margin: 0 0 1rem 0;
            color: #333;
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, S25DropdownPaginatedModule, S25ButtonComponent, S25IconComponent],
})
export class S25ObjectOccurrenceFieldComponent implements OnInit {
    value = input<ObjectOccurrenceData | undefined>(undefined); // ObjectOccurrenceData object
    readOnly = input<boolean>(false);
    valueChange = output<ObjectOccurrenceData | undefined>(); // Emits ObjectOccurrenceData object

    newStartDate: Proto.ISODateString = "";
    newEndDate: Proto.ISODateString = "";

    occurrences = signal<ObjectOccurrence[]>([]);
    locationNames = signal<{ [locationId: number]: string }>({});
    dateTimeFormat = signal<string | undefined>(undefined);

    dateWarning = computed(() => {
        if (!this.newStartDate || !this.newEndDate) {
            return null;
        }
        const start = S25Util.date.parse(this.newStartDate);
        const end = S25Util.date.parse(this.newEndDate);
        const startStr = S25Util.date.toS25ISODateStr(start);
        const endStr = S25Util.date.toS25ISODateStr(end);
        if (startStr !== endStr) {
            return "Warning: Start and end dates are on different days!";
        }
        return null;
    });

    selectedSearch = signal<SearchItem | undefined>(undefined);
    nameSearchText = "";
    searching = signal<boolean>(false);
    hasSearched = signal<boolean>(false);
    locationResults = signal<LocationSearchResult[]>([]);
    searchMethod = signal<"saved" | "name">("saved"); // Default to saved search

    totalOccurrences = computed(() => this.occurrences().length);

    // Expose Item for template access
    readonly Item = Item;

    constructor(private cd: ChangeDetectorRef) {
        // Watch for changes to occurrences and emit when they change
        effect(() => {
            this.emitChange(this.occurrences());
        });
    }

    async ngOnInit() {
        this.loadValue(this.value());

        // Fetch datetime format once on init
        const [dateTimeFormat] = await Promise.all([UserprefService.getS25DateTimeformat()]);
        this.dateTimeFormat.set(dateTimeFormat);
    }

    loadValue(data: ObjectOccurrenceData | undefined) {
        if (!data || !data.occurrences) {
            this.occurrences.set([]);
            return;
        }
        this.occurrences.set(data.occurrences || []);
        void this.loadLocationNames();
    }

    async loadLocationNames() {
        const allLocationIds = this.occurrences()
            .flatMap((occ) => occ.locationIds)
            // remove duplicates
            .filter((id, index, arr) => arr.indexOf(id) === index);

        if (allLocationIds.length === 0) {
            return;
        }

        // Only fetch names for location IDs that we don't already have
        const currentNames = this.locationNames();
        const missingIds = allLocationIds.filter((id) => !currentNames[id]);

        if (missingIds.length === 0) {
            return; // All names already loaded
        }

        const names = await NameService.getNames(
            missingIds.map((id) => ({ itemId: id, itemTypeId: Item.Ids.Location })),
        );

        // Merge new names with existing names
        const updatedNames = { ...currentNames };
        missingIds.forEach((id) => {
            updatedNames[id] = names[`${id}-${Item.Ids.Location}`] || "Unknown Location";
        });
        this.locationNames.set(updatedNames);
    }

    getLocationName(locationId: number): string {
        return this.locationNames()[locationId] || "Loading...";
    }

    formatDatetime(isoString: Proto.ISODateString): string {
        const date = new Date(isoString);
        const format = this.dateTimeFormat();
        if (format) {
            return S25Datefilter.transform(isoString, format) as string;
        }
        return date.toLocaleString();
    }

    addOccurrence = async () => {
        if (!this.newStartDate || !this.newEndDate) {
            alert("Please select both start and end dates");
            return;
        }

        const startDate = S25Util.date.parse(this.newStartDate);
        const endDate = S25Util.date.parse(this.newEndDate);

        if (endDate <= startDate) {
            alert("End date must be after start date");
            return;
        }

        const newOcc: ObjectOccurrence = {
            occurrenceUuid: `occ_${Date.now()}`,
            startDate: S25Util.date.toS25ISODateTimeStrFromDate(startDate),
            endDate: S25Util.date.toS25ISODateTimeStrFromDate(endDate),
            locationIds: [],
        };

        // If there are existing occurrences, try to add their location IDs
        if (this.occurrences().length > 0) {
            const allUniqueLocationIds = Array.from(new Set(this.occurrences().flatMap((occ) => occ.locationIds)));
            if (allUniqueLocationIds.length > 0) {
                const availabilityMap = await this.checkAvailability(allUniqueLocationIds, [newOcc]);
                const availableIds = Array.from(availabilityMap.get(newOcc.occurrenceUuid) || []);
                newOcc.locationIds = availableIds;
                await this.loadLocationNames();
            }
        }

        this.occurrences.update((occs) => [...occs, newOcc]);
        this.newStartDate = "";
        this.newEndDate = "";
        this.cd.detectChanges();
    };

    removeOccurrence = (occurrenceUuid: string) => {
        this.occurrences.update((occs) => occs.filter((occ) => occ.occurrenceUuid !== occurrenceUuid));
    };

    onSearchMethodChange = (method: "saved" | "name") => {
        this.searchMethod.set(method);
        if (method === "saved") {
            // Clear name search
            this.nameSearchText = "";
        } else {
            // Clear saved search
            this.clearSearchSelection();
        }
    };

    clearSearchSelection = () => {
        this.selectedSearch.set(undefined);
        this.locationResults.set([]);
        this.hasSearched.set(false);
    };

    onSearchSelected = async (search: SearchItem) => {
        if (!search) {
            return;
        }

        // Clear name search when saved search is selected
        this.nameSearchText = "";
        this.selectedSearch.set(search);

        let searchQuery: string = String(search.query_id || search.query || search.val || "");
        if (!searchQuery.startsWith("&")) {
            searchQuery = `&query_id=${searchQuery}`;
        }
        searchQuery = searchQuery.replace("&spaces_", "&");
        await this.fetchLocationsFromSearch(searchQuery);
    };

    searchByName = async () => {
        if (!this.nameSearchText.trim()) {
            alert("Please enter a location name to search");
            return;
        }
        const searchQuery = `&name=${encodeURIComponent(this.nameSearchText.trim())}`;
        await this.fetchLocationsFromSearch(searchQuery);
    };

    private async fetchLocationsFromSearch(searchQuery: string) {
        this.searching.set(true);
        this.hasSearched.set(false);

        try {
            // Fetch spaces using the search query
            const spacesData = await SpaceService.getSpacesBySearchQuery(searchQuery);
            const spaces = spacesData?.spaces?.space || [];

            if (spaces.length === 0) {
                this.locationResults.set([]);
                this.hasSearched.set(true);
                this.searching.set(false);
                return;
            }

            // Extract location IDs and names from response
            const locationData = spaces.map((space: S25WsSpace) => ({
                locationId: parseInt(String(space.space_id)),
                locationName: String(space.space_name || space.formal_name || `Location ${space.space_id}`),
            }));

            const locationIds = locationData.map((data) => data.locationId);

            // Check availability for all occurrences
            const availabilityMap = await this.checkAvailability(locationIds, this.occurrences());

            // Build results with availability info
            const results: LocationSearchResult[] = [];
            for (const { locationId, locationName } of locationData) {
                const availableOccurrenceUuids: string[] = [];

                for (const occ of this.occurrences()) {
                    if (availabilityMap.get(occ.occurrenceUuid)?.has(locationId)) {
                        availableOccurrenceUuids.push(occ.occurrenceUuid);
                    }
                }

                // Only include locations with at least one available occurrence
                if (availableOccurrenceUuids.length > 0) {
                    results.push({
                        locationId,
                        locationName,
                        availableCount: availableOccurrenceUuids.length,
                        availableOccurrenceUuids,
                    });
                }
            }

            // Sort by available count (descending), then by name (ascending, case-insensitive)
            results.sort((a, b) => {
                if (b.availableCount !== a.availableCount) {
                    return b.availableCount - a.availableCount;
                }
                return a.locationName.toLowerCase().localeCompare(b.locationName.toLowerCase());
            });

            this.locationResults.set(results);
            this.hasSearched.set(true);
        } catch (error) {
            console.error("Error fetching locations:", error);
            this.locationResults.set([]);
            this.hasSearched.set(true);
        } finally {
            this.searching.set(false);
        }
    }

    addLocationToAvailable = async (locationId: number, availableOccurrenceUuids: string[], locationName: string) => {
        // Add location to all occurrences for which it's available
        for (const occ of this.occurrences()) {
            if (availableOccurrenceUuids.includes(occ.occurrenceUuid) && !occ.locationIds.includes(locationId)) {
                occ.locationIds.push(locationId);
            }
        }

        this.occurrences.update((occs) => [...occs]);

        // Update locationNames signal directly to avoid fetching
        this.locationNames.update((names) => ({
            ...names,
            [locationId]: locationName,
        }));

        // Remove from results list
        this.locationResults.update((results) => results.filter((r) => r.locationId !== locationId));
        this.cd.detectChanges();
    };

    removeLocation = (occurrenceUuid: string, locationId: number) => {
        const occurrence = this.occurrences().find((occ) => occ.occurrenceUuid === occurrenceUuid);
        if (occurrence) {
            occurrence.locationIds = occurrence.locationIds.filter((id) => id !== locationId);
            this.occurrences.update((occs) => [...occs]);
        }
    };

    /**
     * Core availability checking function - checks multiple locations across multiple occurrences
     * Returns a map of occurrenceUuid -> Set of available location IDs
     */
    private async checkAvailability(
        locationIds: number[],
        occurrences: ObjectOccurrence[],
    ): Promise<Map<string, Set<number>>> {
        // Map from occurrenceUuid (string) to Set of available location IDs (number)
        const result = new Map<string, Set<number>>();

        if (locationIds.length === 0 || occurrences.length === 0) {
            occurrences.forEach((occ) => result.set(occ.occurrenceUuid, new Set()));
            return result;
        }

        const dates = occurrences.map((occ) => ({
            start_dt: occ.startDate,
            end_dt: occ.endDate,
            ext_uid: occ.occurrenceUuid,
        }));

        // Make ONE request with all locations at once
        const request: SpaceAvailabilityRequest = {
            space_availability: {
                event: {
                    event_id: -1,
                    profile: {
                        profile_id: -1,
                    },
                },
                requirements: {
                    space: locationIds.map((locationId) => ({ space_id: locationId })),
                    allow_sharing: "F",
                },
                dates: dates,
            },
        };

        try {
            // Make single request for all locations
            const response = await SpaceService.checkSpaceAvailability(request, false, false, false);

            if (response.error) {
                console.error("Error checking availability:", response.error);
                occurrences.forEach((occ) => result.set(occ.occurrenceUuid, new Set()));
                return result;
            }

            // Initialize result map with empty sets
            occurrences.forEach((occ) => result.set(occ.occurrenceUuid, new Set()));

            // Group conflicts and reservations by location ID (roomId)
            const conflictsByLocation = new Map<number, SpConflict[]>();
            const reservationsByLocation = new Map<number, SpReservation[]>();

            // Group conflicts by roomId
            response.data?.spConflicts?.forEach((conflict) => {
                const roomId = conflict.roomId;
                if (!conflictsByLocation.has(roomId)) {
                    conflictsByLocation.set(roomId, []);
                }
                conflictsByLocation.get(roomId)!.push(conflict);
            });

            // Group reservations by roomId
            response.data?.apSpReservations?.forEach((res) => {
                const roomId = res.roomId;
                if (!reservationsByLocation.has(roomId)) {
                    reservationsByLocation.set(roomId, []);
                }
                reservationsByLocation.get(roomId)!.push(res);
            });

            // For each location, check availability for each occurrence
            locationIds.forEach((locationId) => {
                const locationConflicts = conflictsByLocation.get(locationId) || [];
                const locationReservations = reservationsByLocation.get(locationId) || [];

                occurrences.forEach((occ) => {
                    if (!result.get(occ.occurrenceUuid)) {
                        result.set(occ.occurrenceUuid, new Set());
                    }

                    // Check for conflicts for this specific occurrence
                    const hasConflicts = locationConflicts.some(
                        (conflict) => conflict.candidateRsrvExtUid === occ.occurrenceUuid,
                    );

                    // Check for AP policy restrictions for this specific occurrence
                    const blockedByAp = locationReservations.some(
                        (res) =>
                            res.extUid === occ.occurrenceUuid &&
                            res.apInfo &&
                            !res.apInfo.canRequest &&
                            !res.apInfo.canAssign,
                    );

                    if (!hasConflicts && !blockedByAp) {
                        result.get(occ.occurrenceUuid)!.add(locationId);
                    }
                });
            });

            return result;
        } catch (e) {
            console.error("Error checking availability:", e);
            occurrences.forEach((occ) => result.set(occ.occurrenceUuid, new Set()));
            return result;
        }
    }

    emitChange(occurrences: ObjectOccurrence[]) {
        this.valueChange.emit({ occurrences: occurrences });
    }
}
